import type { QuickFillItem } from '../../../shared/types';

export interface ResolveQuickFillModelSelectionRowsInput {
  contextMenuOpen: boolean;
  contextMenuRowIndex: number | null;
  selectedCellRowIndices: number[];
  navigationRequestRowIndex: number | null;
  selectedRows: number[];
  rowCount: number;
}

export interface QuickFillModelSelectionElement {
  elementId?: string | null;
  familyName?: string | null;
  typeName?: string | null;
  typeId?: string | null;
}

const normalizeModelSelectionToken = (value?: string | null): string =>
  (value || '').trim().toLowerCase();

const addModelElementId = (ids: Set<string>, value: unknown): void => {
  if (typeof value !== 'string') {
    return;
  }

  const trimmed = value.trim();
  if (trimmed) {
    ids.add(trimmed);
  }
};

export const resolveQuickFillModelSelectionRowIndices = ({
  contextMenuOpen,
  contextMenuRowIndex,
  selectedCellRowIndices,
  navigationRequestRowIndex,
  selectedRows,
  rowCount,
}: ResolveQuickFillModelSelectionRowsInput): number[] => {
  // Context-menu and checkbox scopes are deliberate selections. Cell and
  // navigation state can persist after them, so those fallbacks rank lower.
  const sourceRows =
    contextMenuOpen && contextMenuRowIndex != null
      ? [contextMenuRowIndex]
      : selectedRows.length > 0
        ? selectedRows
        : selectedCellRowIndices.length > 0
          ? selectedCellRowIndices
          : navigationRequestRowIndex != null
            ? [navigationRequestRowIndex]
            : [];

  return Array.from(
    new Set(
      sourceRows.filter(
        (rowIndex) =>
          Number.isInteger(rowIndex) &&
          rowIndex >= 0 &&
          rowIndex < rowCount
      )
    )
  ).sort((left, right) => left - right);
};

const parseQuickFillFamilyTypeName = (
  name: string
): { familyName: string; typeName: string | null } => {
  const separatorIndex = name.indexOf(':');
  if (separatorIndex < 0) {
    return { familyName: name.trim(), typeName: null };
  }

  return {
    familyName: name.slice(0, separatorIndex).trim(),
    typeName: name.slice(separatorIndex + 1).trim() || null,
  };
};

const getQuickFillRowFamilyTypeParts = (
  row: QuickFillItem
): { familyName: string; typeName: string | null } => {
  const familyTypeKey = (row as any)?.__familyTypeKey;
  if (typeof familyTypeKey === 'string' && familyTypeKey.trim()) {
    return parseQuickFillFamilyTypeName(familyTypeKey);
  }

  return parseQuickFillFamilyTypeName(row.Name || '');
};

export const resolveQuickFillRowModelElementIds = (
  row: QuickFillItem | undefined,
  pipeElements: QuickFillModelSelectionElement[]
): string[] => {
  if (!row) {
    return [];
  }

  const ids = new Set<string>();
  addModelElementId(ids, (row as any).__elementId);
  if ((row as any).__rowMode === 'instance') {
    return Array.from(ids);
  }

  const instanceTargets = (row as any).__instanceTargetElementIdsByField;
  if (instanceTargets && typeof instanceTargets === 'object') {
    Object.values(instanceTargets).forEach((fieldTargets) => {
      if (!Array.isArray(fieldTargets)) {
        return;
      }

      fieldTargets.forEach((targetId) => addModelElementId(ids, targetId));
    });
  }

  const familyTypeId = normalizeModelSelectionToken((row as any).__familyTypeId);
  const { familyName, typeName } = getQuickFillRowFamilyTypeParts(row);
  const familyKey = normalizeModelSelectionToken(familyName);
  const typeKey = normalizeModelSelectionToken(typeName);

  pipeElements.forEach((element) => {
    const elementId = typeof element?.elementId === 'string' ? element.elementId.trim() : '';
    if (!elementId) {
      return;
    }

    const elementTypeId = normalizeModelSelectionToken(element.typeId);
    if (familyTypeId && elementTypeId && elementTypeId === familyTypeId) {
      ids.add(elementId);
      return;
    }

    if (!familyKey) {
      return;
    }

    const elementFamilyKey = normalizeModelSelectionToken(element.familyName);
    const elementTypeKey = normalizeModelSelectionToken(element.typeName);
    if (
      elementFamilyKey === familyKey &&
      (!typeKey || elementTypeKey === typeKey)
    ) {
      ids.add(elementId);
    }
  });

  return Array.from(ids);
};

export const resolveQuickFillSelectionModelElementIds = (
  quickFillData: QuickFillItem[],
  rowIndices: number[],
  pipeElements: QuickFillModelSelectionElement[]
): string[] => {
  const ids = new Set<string>();

  rowIndices.forEach((rowIndex) => {
    if (!Number.isInteger(rowIndex) || rowIndex < 0) {
      return;
    }

    resolveQuickFillRowModelElementIds(quickFillData[rowIndex], pipeElements)
      .forEach((elementId) => ids.add(elementId));
  });

  return Array.from(ids);
};
